import ChevronLeftIcon from '@ordr-as/icons/ChevronLeftIcon';
import { Dialog } from '@ordr-as/umami-react/dialog';
import { useCustomisations } from '../../../hooks/useCustomisations';
import { FC, useCallback, useState, useMemo } from 'react';
import { MenuItemVariantDto, MenuBuilderComponentDto } from '../../../../generated-api';
import { AddComponentDto, AddItemsDto, CartAdditionOption } from '../../../../types/cart';
import useIntl from '../../../hooks/useIntl';
import { handleMissingCustomizationScroll, isRequiredHandled } from '../../../utils/customizations';
import OptionGroupSection from './customization/OptionGroupSection';
import { useScreenSize } from '../../../context/ScreenSizeContext';
import React from 'react';
import { useCartStore } from '../../../context/cart/CartStore';
import { ResolvedComponent, collectCustomizableComponents } from '../../../hooks/useResolvedComponents';
import { useAllProducts } from '../../../hooks/useAllProducts';
import { buildAddComponentDto } from '../../../utils/cart';

interface Props {
  onModalClose: VoidFunction;
  isOpen: boolean;
  item: MenuItemVariantDto;
  componentProducts: ResolvedComponent[];
}

const CompositeItemModal: FC<Props> = ({ isOpen, item, componentProducts, onModalClose }) => {
  const { isPhone } = useScreenSize();
  const addMenuItems = useCartStore((x) => x.addMenuItems);
  const allProducts = useAllProducts();
  const intl = useIntl();

  // Determine which components need customization (have option groups), recursively
  const customizableComponents = useMemo(() => {
    return collectCustomizableComponents(componentProducts, allProducts);
  }, [componentProducts, allProducts]);

  // Check if parent needs customization
  const parentNeedsCustomization = item.optionGroups.length > 0;

  // Build list of all items that need customization steps
  const customizationSteps = useMemo(() => {
    const steps: Array<{ item: MenuItemVariantDto; isParent: boolean; componentDto?: MenuBuilderComponentDto }> = [];
    if (parentNeedsCustomization) {
      steps.push({ item, isParent: true });
    }
    customizableComponents.forEach((cp) => {
      steps.push({ item: cp.component, isParent: false, componentDto: cp.componentDto });
    });
    return steps;
  }, [item, parentNeedsCustomization, customizableComponents]);

  // State management
  const [currentStep, setCurrentStep] = useState(0);
  const quantity = 1;
  const [parentOptions, setParentOptions] = useState<CartAdditionOption[]>([]);
  const [componentCustomizations, setComponentCustomizations] = useState<Map<string, CartAdditionOption[]>>(new Map());

  // Current item being customized
  const currentStepInfo = customizationSteps[currentStep];
  const currentItem = currentStepInfo?.item;

  // Current options for the current step
  const currentOptions = useMemo(() => {
    if (!currentStepInfo) return [];
    if (currentStepInfo.isParent) {
      return parentOptions;
    }
    const componentId = currentStepInfo.item.variantLinkId;
    return componentCustomizations.get(componentId) || [];
  }, [currentStepInfo, parentOptions, componentCustomizations]);

  // Update options for current step
  const updateCurrentOptions = useCallback(
    (optionsOrFn: CartAdditionOption[] | ((prev: CartAdditionOption[]) => CartAdditionOption[])) => {
      if (!currentStepInfo) {
        return;
      }

      if (currentStepInfo.isParent) {
        setParentOptions((prev) => {
          return typeof optionsOrFn === 'function' ? optionsOrFn(prev) : optionsOrFn;
        });

        return;
      }

      const componentId = currentStepInfo.item.variantLinkId;
      setComponentCustomizations((prev) => {
        const currentOptions = prev.get(componentId) || [];
        const newOptions = typeof optionsOrFn === 'function' ? optionsOrFn(currentOptions) : optionsOrFn;
        const updated = new Map(prev);
        updated.set(componentId, newOptions);
        return updated;
      });
    },
    [currentStepInfo]
  );

  // Validation for current step
  const { hasAddedAllRequiredOptionGroups, requiredOptionGroups, addedRequiredOptionGroups } = useCustomisations(
    currentItem!,
    currentOptions
  );

  // Total number of steps
  const totalSteps = customizationSteps.length;
  const isLastStep = currentStep === totalSteps - 1;

  const handleNext = useCallback(() => {
    if (!hasAddedAllRequiredOptionGroups) {
      handleMissingCustomizationScroll(requiredOptionGroups, addedRequiredOptionGroups);
      return;
    }

    if (isLastStep) {
      // Build nested components array (recursively resolves sub-composites)
      const components: AddComponentDto[] = componentProducts.map((compProduct) =>
        buildAddComponentDto(
          compProduct.componentDto,
          allProducts,
          componentCustomizations,
          item.menuSectionId,
          item.sectionProductId
        )
      );

      // Add parent composite product with nested components
      const addItem: AddItemsDto = {
        item: {
          itemVariantId: item.variantLinkId,
          name: item.name,
          price: item.price,
          takeawayPrice: item.takeawayPrice,
          menuSectionId: item.menuSectionId,
          sectionProductId: item.sectionProductId,
          courseId: item.courseId,
          components: components,
          options: parentOptions.map((option) => ({
            name: option.option.name,
            itemVariantId: option.option.optionId,
            type: option.type,
            quantity: option.quantity,
            price: option.option.price,
            takeawayPrice: option.option.takeawayPrice,
          })),
        },
        quantity: quantity,
      };

      addMenuItems(addItem);
      onModalClose();
      return;
    }

    setCurrentStep((prev) => prev + 1);
  }, [
    hasAddedAllRequiredOptionGroups,
    requiredOptionGroups,
    addedRequiredOptionGroups,
    isLastStep,
    item,
    parentOptions,
    addMenuItems,
    allProducts,
    componentCustomizations,
    componentProducts,
    onModalClose,
  ]);

  const handleBack = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  }, [currentStep]);

  if (!isOpen || !currentItem) {
    return null;
  }

  const stepLabel = currentStep === 0 ? item.name : `${currentItem.name}`;

  const actionLabel = isLastStep ? intl.formatMessage({ id: 'items.addToCartButton' }) : 'Next';

  const splitLines = (description?: string): string[] => {
    if (!description) {
      return [''];
    }
    const lines = description.split(/\r?\n/).filter((line) => line.trim().length > 0);
    return lines.length > 0 ? lines : [''];
  };

  const descriptions = splitLines(currentItem.description)
    .filter((x) => x.trim().length > 0)
    .map((line) => {
      return (
        <p key={line} className="mb-3">
          {line}
        </p>
      );
    });

  const allergens = currentItem.allergens.map((a) => a.name).join(', ');

  const hasDescriptionsOrAllergens = descriptions.length > 0 || allergens.length > 0;

  return (
    <Dialog
      onClose={onModalClose}
      title={stepLabel}
      actionLabel={actionLabel}
      onAction={handleNext}
      isDisabledAction={!hasAddedAllRequiredOptionGroups}
      secondaryActionLabel={currentStep > 0 ? intl.formatMessage({ id: 'generic.back' }) : undefined}
      onSecondaryAction={currentStep > 0 ? handleBack : undefined}
      size={isPhone ? 'fullwidth' : 'default'}
      TitleIcon={currentStep > 0 ? ChevronLeftIcon : undefined}
      titleIconAction={currentStep > 0 ? handleBack : undefined}
      titleIconClassNames={currentStep > 0 ? '!mr-1 !p-0' : undefined}
      footer={totalSteps > 1 ? <StepDots totalSteps={totalSteps} currentStep={currentStep} /> : undefined}
      actionBarClassName="flex flex-col"
    >
      <article className="flex flex-1 min-h-0 flex-col gap-4 overflow-auto">
        {hasDescriptionsOrAllergens && (
          <section>
            {descriptions}
            <p className="text-grey-dark text-small">{allergens}</p>
          </section>
        )}

        {currentItem.optionGroups.map((optionGroup) => (
          <OptionGroupSection
            key={optionGroup.variantLinkId}
            optionGroup={optionGroup}
            setOptions={updateCurrentOptions}
            defaultOptions={currentOptions}
            isRequiredHandled={isRequiredHandled(optionGroup, currentOptions)}
          />
        ))}
      </article>
      {/* {totalSteps > 1 && <StepDots totalSteps={totalSteps} currentStep={currentStep} />} */}
    </Dialog>
  );
};

const StepDots: FC<{ totalSteps: number; currentStep: number }> = ({ totalSteps, currentStep }) => (
  <div className="shrink-0 pt-4">
    <div className="flex justify-center gap-2">
      {Array.from({ length: totalSteps }, (_, i) => (
        <span
          key={i}
          className={`inline-block h-2 w-2 rounded-full ${i === currentStep ? 'bg-brand-primary' : 'bg-grey-medium'}`}
        />
      ))}
    </div>
  </div>
);

export default React.memo(CompositeItemModal);
